/**
 * Module responsible for extracting the necessary data for Analytics before calling Rudderstack client
 * - There are some cases where the data is on the DOM as data attributes ie: data-analytics-<section_name>-<attribute_identificator>
 *
 */

import { TRACK_CART_SOURCE, TRACK_COLLECTION_FILTER_TYPE } from '~/constants/rudderstack';
import * as DataDogLogs from '~/lib/datadog/datadog';
import {
  gtmCartViewed,
  gtmProductAdded,
  gtmProductRemoved,
  gtmProductsSearched,
  gtmProductViewed,
} from '~/lib/gtm';
import * as RudderstackClient from '~/lib/rudderstack/rudderstack-client';
import * as RudderstackUtils from '~/lib/rudderstack/rudderstack-utils';
import * as UrlUtils from '~/utils/url';
import * as ValidatorUtils from '~/utils/validator';

/**
 * Get the navigation source as current User page URL
 * ie: https://www.allbirds.com/products/mens-wool-crew-socks
 *
 * @returns {string} The navigation source
 */
const _getNavigationSource = () => `${window.location.hostname}${window.location.pathname}`;

/**
 * Warn on a log when the props object has missing property, probably due to missed data-analytics attribute
 *
 * @param {Object} param
 * @property {Object} propsObject - The object with the data attributes
 * @property {string} sectionName - The name of the section to hook the analytics events
 */
const _warnMissedDataAttributesProps = ({ propsObject, sectionName = 'not_provided' }) => {
  Object.entries(propsObject).forEach(([key, value]) => {
    if (ValidatorUtils.isNil(value)) {
      DataDogLogs.warn(
        `[Allbirds analytics] - Data Extraction - warnMissedDataAtrtibutesProps - ${key} is missed`,
        { sectionName },
      );
    }
  });
};

/**
 * Get the section container from the DOM
 *
 * @param {string} querySelector - The query selector to use to get the section container
 *
 * @returns {Element} The section container
 */
const _getMainSectionContainer = (querySelector) => {
  const sectionContainer = document.querySelector(querySelector);
  if (ValidatorUtils.isNil(sectionContainer)) {
    throw new Error(`Section container not found for query selector: ${querySelector}`);
  }

  return sectionContainer;
};

/**
 * Function used on Homepage sections to append Rudderstack Query String API event 'Homepage Module Clicked'
 *  to all the anchor elements in the section that have the data attributes
 *
 * This function will add the query string to the CTA links in the section or leave the original rendered by SSR liquid.
 *
 * Use this funciton on onMounted lifecycle hook of the Vue app.
 *
 * How to use this method:
 *  1. Add the data attributes to the section or blocks markup where the CTAs are rendered
 *  2. The data attributes should follow the naming convention 'data-analytics-<section_name>-<attribute_identificator>'
 *     ie: This is an example of data attributes for the section 'color-grid':
 *
 *       data-analytics-color-grid-section-container
 *       data-analytics-color-grid-section-name
 *       data-analytics-color-grid-module-position
 *       data-analytics-color-grid-anchor-element
 *       data-analytics-color-grid-image-name
 *       data-analytics-color-grid-cta-name
 *       data-analytics-color-grid-cta-url
 *       data-analytics-color-grid-card-name
 *       data-analytics-color-grid-card-position
 *
 * @param {Object} param
 * @property {string} sectionName - The name of the section to hook the analytics events
 */
const hookHomepageModuleClicked = ({ sectionName }) => {
  if (ValidatorUtils.isNil(sectionName)) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookHomepageModuleClickedAnalyticsEvents - sectionName is required',
      {},
      new Error('sectionName is required'),
    );

    return;
  }

  try {
    // Get section related data
    const sectionContainer = _getMainSectionContainer(
      `[data-analytics-${sectionName}-section-container]`,
    );
    const analyticModuleName = sectionContainer.getAttribute(
      `data-analytics-${sectionName}-section-name`,
    );
    const analyticModulePosition = sectionContainer.getAttribute(
      `data-analytics-${sectionName}-module-position`,
    );

    // Get all the CTAs in the section
    const anchorElementsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(anchorElementsList).forEach((ctaLink) => {
      const analyticImageName = ctaLink.getAttribute(`data-analytics-${sectionName}-image-name`);
      const analyticCtaName = ctaLink.getAttribute(`data-analytics-${sectionName}-cta-name`);
      const analyticCtaDestination = ctaLink.getAttribute(`data-analytics-${sectionName}-cta-url`);
      const analyticCardName = ctaLink.getAttribute(`data-analytics-${sectionName}-card-name`);
      const analyticCardPosition = ctaLink.getAttribute(
        `data-analytics-${sectionName}-card-position`,
      );

      // Clean the base URL to avoid duplicate query string API parameters
      const cleanedBaseUrl = RudderstackUtils.cleanAllQueryStringAPI(ctaLink.href);
      const baseUrl = cleanedBaseUrl;

      // Warn missed data attributes
      _warnMissedDataAttributesProps({
        propsObject: {
          analyticCardName,
          analyticCardPosition,
          analyticCtaDestination,
          analyticCtaName,
          analyticImageName,
          analyticModuleName,
          analyticModulePosition,
          baseUrl,
        },
        sectionName,
      });

      // Build the URL with the query string
      const ctaUrlWithAnalytics = RudderstackClient.homepageModuleClickQueryStringUrlBuilder({
        baseUrl,
        carouselCardName: analyticCardName,
        carouselCardPosition: analyticCardPosition,
        imageName: analyticImageName,
        linkCTAName: analyticCtaName,
        linkDestination: analyticCtaDestination,
        moduleName: analyticModuleName,
        modulePosition: analyticModulePosition,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookHomepageModuleClickedAnalitycsEvents - Error while hooking the analytics events',
      { sectionName },
      error,
    );
  }
};

/**
 * Function used on Homepage hero section to append Rudderstack Query String API event 'Homepage Hero Clicked'
 *  to all the anchor elements in the section that have the data attributes
 *
 * The anchor element should have the following data attributes:
 * - data-analytics-<section_name>-anchor-element
 * - data-analytics-<section_name>-image-name
 * - data-analytics-<section_name>-cta-name
 * - data-analytics-<section_name>-cta-url
 *
 * @param {Object} param
 * @property {string} sectionName - The name of the section to hook the analytics events
 */
const hookHomepageHeroClicked = ({ sectionName }) => {
  try {
    if (ValidatorUtils.isNil(sectionName)) {
      DataDogLogs.error(
        '[Allbirds analytics] - Data Extraction - hookHomepageHeroClicked - sectionName is required',
        {},
        new Error('sectionName is required'),
      );

      return;
    }

    // Get main section container
    const sectionContainer = _getMainSectionContainer(
      `[data-analytics-${sectionName}-section-container]`,
    );

    // Get all the CTAs in the hero section
    const homepageHeroCTAsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(homepageHeroCTAsList).forEach((ctaLink) => {
      const analyticsImageName = ctaLink.getAttribute(`data-analytics-${sectionName}-image-name`);
      const analyticCtaName = ctaLink.getAttribute(`data-analytics-${sectionName}-cta-name`);
      const analyticCtaLink = ctaLink.getAttribute(`data-analytics-${sectionName}-cta-url`);

      const baseUrl = ctaLink.href;

      // Warn missed data attributes
      _warnMissedDataAttributesProps({
        propsObject: {
          analyticCtaLink,
          analyticCtaName,
          analyticsImageName,
          baseUrl,
        },
        sectionName,
      });

      const ctaUrlWithAnalytics = RudderstackClient.homepageHeroClickQueryStringUrlBuilder({
        baseUrl,
        heroImageName: analyticsImageName,
        linkCTAName: analyticCtaName,
        linkDestination: analyticCtaLink,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookHomepageHeroClicked - Error while hooking the analytics events',
      { sectionName },
      error,
    );
  }
};

/**
 * Function used to trigger the Rudderstack analytics event 'Navigation Clicked'
 *
 * @param {Object} param
 * @property {string} navigationLevel1
 * @property {string} navigationLevel2
 * @property {string} navigationLevel3
 * @property {string} navigationType
 */
const triggerNavigationViewed = ({
  navigationLevel1,
  navigationLevel2,
  navigationLevel3,
  navigationType,
}) => {
  try {
    RudderstackClient.navigationViewed({
      navigationLevel1,
      navigationLevel2,
      navigationLevel3,
      navigationSource: _getNavigationSource(),
      navigationType,
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerNavigationViewed - Error while calling the analytics events',
      {},
      error,
    );
  }
};

/**
 * Function used on any section to append Rudderstack Query String API event 'Navigation Clicked'
 *  to all the anchor elements in the section that have the data attributes
 *
 * The anchor element should have the following data attributes:
 * - data-analytics-<section_name>-anchor-element
 * - data-analytics-<section_name>-navigation-level-1
 * - data-analytics-<section_name>-navigation-level-2
 * - data-analytics-<section_name>-navigation-level-3
 * - data-analytics-<section_name>-navigation-type
 *
 * @param {Object} param
 * @property {string} sectionName - The name of the section to hook the analytics events
 */
const hookNavigationClicked = ({ sectionName }) => {
  try {
    if (ValidatorUtils.isNil(sectionName)) {
      DataDogLogs.error(
        '[Allbirds analytics] - Data Extraction - hookNavigationClicked - sectionName is required',
        {},
        new Error('sectionName is required'),
      );

      return;
    }

    // Get main section container
    const sectionContainer = _getMainSectionContainer(
      `[data-analytics-${sectionName}-section-container]`,
    );

    // Get all the CTAs in the hero section
    const anchorElementsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(anchorElementsList).forEach((ctaLink) => {
      const analyticsNavigationLevel1 = ctaLink.getAttribute(
        `data-analytics-${sectionName}-navigation-level-1`,
      );
      const analyticsNavigationLevel2 = ctaLink.getAttribute(
        `data-analytics-${sectionName}-navigation-level-2`,
      );
      const analyticsNavigationLevel3 = ctaLink.getAttribute(
        `data-analytics-${sectionName}-navigation-level-3`,
      );

      const analyticsNavigationType = ctaLink.getAttribute(
        `data-analytics-${sectionName}-navigation-type`,
      );

      const baseUrl = ctaLink.href;

      // Warn mandatory missed data attributes
      _warnMissedDataAttributesProps({
        propsObject: {
          analyticsNavigationType,
          baseUrl,
        },
        sectionName,
      });

      const ctaUrlWithAnalytics = RudderstackClient.navigationClickedQueryStringUrlBuilder({
        baseUrl,
        navigationLevel1: analyticsNavigationLevel1,
        navigationLevel2: analyticsNavigationLevel2,
        navigationLevel3: analyticsNavigationLevel3,
        navigationSource: _getNavigationSource(),
        navigationType: analyticsNavigationType,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookNavigationClicked - Error while hooking the analytics events',
      { sectionName },
      error,
    );
  }
};

/**
 * Collect product data and size selected data and trigger the product viewed event
 * If the variant size is provided, use it, otherwise attempt to get it from the query string
 * If the variant size contains a dot, replace it with a dash to standardize the size
 *
 * @param {Object} param
 * @property {string} variantSize - The size of the variant to be used in the product viewed event
 */
const triggerProductViewed = ({ variantSize = null } = {}) => {
  try {
    const product = window.ALLBIRDS?.page?.productDetailsPage?.product;
    let sizeSelected = variantSize;

    // If the variant size is not provided, attempt to get it from the query string
    if (ValidatorUtils.isNil(variantSize)) {
      const currentQueryString = UrlUtils.parseQueryString();
      sizeSelected = currentQueryString['size'];
    }

    // If size was selected attempt to replace dots with dash to standardize the size
    if (sizeSelected) {
      sizeSelected = RudderstackUtils.getNormalizedSize(sizeSelected);
    }

    RudderstackClient.productViewed({
      product,
      sizeSelected,
    });
    gtmProductViewed(product, sizeSelected);
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerProductViewed - Error while triggering the product viewed event',
      {},
      error,
    );
  }
};

/**
 * Collect collection data and trigger the product list viewed event
 */
const triggerProductListViewed = () => {
  try {
    const collection = window.ALLBIRDS?.page?.collectionPage?.collection;

    RudderstackClient.productListViewed({
      collection,
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerProductListViewed - Error while triggering the product list viewed event',
      {},
      error,
    );
  }
};

/**
 * Collect collection data and trigger the product list feature clicked event
 *
 * This event should be triggered when:
 * - User applies a sorting option on the collection page
 * - User switches using gender toggle on the collection page
 * - User lands on the collection page with sorting query string params
 */
const triggerProductListFeatureClicked = ({
  collection,
  featureLinkDestination,
  featureName,
  featureType,
}) => {
  try {
    RudderstackClient.productListFeatureClicked({
      collection,
      featureCTA: featureName,
      featureLinkDestination: featureLinkDestination,
      featureType: getFeatureType(featureType),
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerProductListViewed - Error while triggering the product list viewed event',
      {},
      error,
    );
  }
};

/**
 * Collect collection data with filters applied and trigger the product list filtered event if any filters are applied
 *
 * This event should be triggered when:
 * - User apply a filter on the collection page
 * - User clear all filters on the collection page
 * - User lands on the collection page with filters applied as query string params
 */
const triggerProductListFiltered = () => {
  try {
    const collection = window.ALLBIRDS?.page?.collectionPage?.collection;

    RudderstackClient.productListFiltered({
      collection,
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerProductListFiltered - Error while triggering the product list filtered event',
      {},
      error,
    );
  }
};

/**
 * Get the feature type from the data attribute
 *
 * @param {string} featureType - The feature type from the data attribute
 *
 * @returns {string} The feature type
 */
const getFeatureType = (featureType) => {
  let result = 'feature_type_not_found';

  if (featureType === TRACK_COLLECTION_FILTER_TYPE.PILL_TOGGLE.KEY) {
    result = TRACK_COLLECTION_FILTER_TYPE.PILL_TOGGLE.RUDDERSTACK_NAME;
  } else if (featureType === TRACK_COLLECTION_FILTER_TYPE.SORTING.KEY) {
    result = TRACK_COLLECTION_FILTER_TYPE.SORTING.RUDDERSTACK_NAME;
  }

  return result;
};

/**
 * Function used on any section to append Rudderstack Query String API event 'Product List Clicked'
 *  to all the anchor elements in the section that have the data attributes
 *
 * The anchor element should have the following data attributes:
 * - data-analytics-<section_name>-anchor-element
 * - data-analytics-<section_name>-product-id
 * - data-analytics-<section_name>-card-position
 *
 * @param {Object} param
 * @property {string} sectionName - The name of the section to hook the analytics events
 * @property {Object} collection - Allbirds collection representation object, check 'utils.js-collection-object.liquid'
 */
const hookProductListClicked = ({ collection, sectionName }) => {
  if (ValidatorUtils.isNil(collection) || ValidatorUtils.isNil(sectionName)) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookProductListClicked - collection and sectionName are required',
      { collection, sectionName },
      new Error('Missing required params'),
    );

    return;
  }

  try {
    // Get main section container
    const sectionContainer = _getMainSectionContainer(
      `[data-analytics-${sectionName}-section-container]`,
    );

    // Get all the CTAs in the hero section
    const anchorElementsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(anchorElementsList).forEach((ctaLink) => {
      const analyticsProductClickedId = ctaLink.getAttribute(
        `data-analytics-${sectionName}-product-id`,
      );
      const analyticsProductClickedPosition = ctaLink.getAttribute(
        `data-analytics-${sectionName}-card-position`,
      );

      const baseUrl = ctaLink.href;

      // Warn mandatory missed data attributes
      _warnMissedDataAttributesProps({
        propsObject: {
          analyticsProductClickedId,
          analyticsProductClickedPosition,
          baseUrl,
        },
        sectionName,
      });

      const ctaUrlWithAnalytics = RudderstackClient.productListClickedQueryStringUrlBuilder({
        baseUrl,
        collection,
        productClickedId: analyticsProductClickedId,
        productClickedPosition: analyticsProductClickedPosition,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookProductListClicked - Error while hooking the analytics events',
      { collection, sectionName },
      error,
    );
  }
};

/**
 * Function used on any section to append 'Query String API' for 'Product List Feature Clicked'
 *
 * When 'useDomData' is true, the anchor elements should have the following data attributes:
 * - data-analytics-<section_name>-anchor-element
 * - data-analytics-<section_name>-cta-name
 * - data-analytics-<section_name>-cta-url
 *
 * When 'useDomData' is false, 'featureName' and 'featureUrlObject' are required as params on this function
 *
 * @param {Object} param - Object with several params
 * @property {string} sectionName - The name of the section to hook the analytics events
 * @property {Object} collection - Allbirds collection representation object, check 'utils.js-collection-object.liquid'
 * @property {URL} featureUrlObject - The URL object to be updated with the query params
 * @property {string} featureName - The name of the feature ie: 'WOMEN'
 * @property {string} featureType - The type of the feature ie: 'pill toggle', 'sorting'
 * @property {boolean} useDomData - Whether to use the DOM data or the URL object and params
 */
const hookProductListFeatureClicked = ({
  collection = null,
  featureName = null,
  featureType = null,
  featureUrlObject = null,
  sectionName = null,
  useDomData = true,
}) => {
  // Validate required params for all cases
  if (
    ValidatorUtils.isNil(collection) ||
    ValidatorUtils.isNil(sectionName) ||
    ValidatorUtils.isNil(featureType)
  ) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookProductListFeatureClicked - collection and sectionName are required',
      { collection, sectionName },
      new Error('Missing required params'),
    );

    return;
  }

  // Validate required params if used useDomData is false
  if (!useDomData) {
    if (
      ValidatorUtils.isNil(featureUrlObject) ||
      ValidatorUtils.isNil(featureUrlObject.searchParams) ||
      ValidatorUtils.isNil(featureName)
    ) {
      DataDogLogs.error(
        '[Allbirds analytics] - Data Extraction - hookProductListFeatureClicked - collection and sectionName are required',
        { collection, sectionName },
        new Error('Missing required params for urlObject and featureObj'),
      );

      return;
    }
  }

  try {
    // If the URL object is provided, update the URL with the query params, otherwise, decorate the href on the DOM
    if (useDomData) {
      // Get main section container
      const sectionContainer = _getMainSectionContainer(
        `[data-analytics-${sectionName}-section-container]`,
      );

      // Get all the CTAs in the hero section
      const anchorElementsList = sectionContainer.querySelectorAll(
        `[data-analytics-feature-clicked-${sectionName}-anchor-element]`,
      );

      // For each CTA, get the data attributes and build the URL with the query string
      Array.from(anchorElementsList).forEach((ctaLink) => {
        const analyticsCtaName = ctaLink.getAttribute(
          `data-analytics-feature-clicked-${sectionName}-cta-name`,
        );
        const analyticsCtaUrl = ctaLink.getAttribute(
          `data-analytics-feature-clicked-${sectionName}-cta-url`,
        );

        const baseUrl = ctaLink.href;

        // Warn mandatory missed data attributes
        _warnMissedDataAttributesProps({
          propsObject: {
            analyticsCtaName,
            analyticsCtaUrl,
            baseUrl,
          },
          sectionName,
        });

        const ctaUrlWithAnalytics =
          RudderstackClient.productListFeatureClickedQueryStringUrlBuilder({
            baseUrl,
            collection,
            featureCTA: analyticsCtaName,
            featureLinkDestination: analyticsCtaUrl,
            featureType: getFeatureType(featureType),
          });

        // Only set the href if it is not empty otherwise leave the original href
        if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
      });
    } else {
      // Update provided urlObject with the feature name, type and collection data
      const linkDestinationProcessed = `${featureUrlObject.pathname}${featureUrlObject.search}`;

      const ctaUrlWithAnalytics = RudderstackClient.productListFeatureClickedQueryStringUrlBuilder({
        baseUrl: featureUrlObject.toString(),
        collection,
        featureCTA: featureName,
        featureLinkDestination: linkDestinationProcessed,
        featureType: getFeatureType(featureType),
      });

      // Only update the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) featureUrlObject.href = ctaUrlWithAnalytics;
    }
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookProductListFeatureClicked - Error while hooking the analytics events',
      { collection, sectionName },
      error,
    );
  }
};

/**
 * Function used on any section to append 'Query String API' for 'Global Banner Link Clicked' event
 *
 * - On this use case event, all <a /> elements will be decorated with the Query String API
 * - Tracking Plan: https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
 *
 * @param {Object} param with several properties
 * @property {string} sectionName - The name of the section to hook the analytics events
 *
 * @returns {void}
 */
const hookGlobalBannerLinkClicked = ({ sectionName }) => {
  if (ValidatorUtils.isNil(sectionName)) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookGlobalBannerLinkClicked - sectionName is required',
      { sectionName },
      new Error('Missing required params'),
    );

    return;
  }

  try {
    // Get main section container
    const sectionContainer = _getMainSectionContainer(`[data-analytics-${sectionName}]`);

    // Get only banner-related anchor elements in the section
    const anchorElementsList = sectionContainer.querySelectorAll('a');

    // For each anchor element, get the data attributes and build the URL with the query string
    Array.from(anchorElementsList).forEach((ctaLink) => {
      const analyticsGlobalBannerName = ctaLink?.textContent?.trim();

      const baseUrl = ctaLink.href;

      // Warn mandatory missed data attributes
      _warnMissedDataAttributesProps({
        propsObject: {
          analyticsGlobalBannerName,
          baseUrl,
        },
        sectionName,
      });

      const ctaUrlWithAnalytics = RudderstackClient.globalBannerClickedQueryStringUrlBuilder({
        baseUrl,
        globalBannerName: analyticsGlobalBannerName,
        globalHeaderSource: window.location.href,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookGlobalBannerLinkClicked - Error while hooking the analytics events',
      { sectionName },
      error,
    );
  }
};

/**
 * Function used on any section to append 'Query String API' for event 'Footer Link Clicked'
 *
 * The anchor element should have the following data attributes:
 * - data-analytics-<section_name>-anchor-element
 * - data-analytics-<section_name>-link-category
 * - data-analytics-<section_name>-link-name
 *
 * @param {Object} param with several properties
 * @property {string} sectionName - The name of the section to hook the analytics events
 *
 * @returns {void}
 */
const hookFooterLinkClicked = ({ sectionName }) => {
  if (ValidatorUtils.isNil(sectionName)) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookFooterLinkClicked - sectionName is required',
      { sectionName },
      new Error('Missing required params'),
    );

    return;
  }

  try {
    // Get main section container
    const sectionContainer = _getMainSectionContainer(
      `[data-analytics-${sectionName}-section-container]`,
    );

    // Get all the CTAs in the hero section
    const anchorElementsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(anchorElementsList).forEach((ctaLink) => {
      const analyticsFooterLinkCategory = ctaLink.getAttribute(
        `data-analytics-${sectionName}-link-category`,
      );
      const analyticsFooterLinkName = ctaLink.getAttribute(
        `data-analytics-${sectionName}-link-name`,
      );

      const baseUrl = ctaLink.href;

      // Warn mandatory missed data attributes
      _warnMissedDataAttributesProps({
        propsObject: {
          analyticsFooterLinkCategory,
          analyticsFooterLinkName,
          baseUrl,
        },
        sectionName,
      });

      const ctaUrlWithAnalytics = RudderstackClient.footerLinkClickedQueryStringUrlBuilder({
        baseUrl,
        footerLinkCategory: analyticsFooterLinkCategory,
        footerLinkName: analyticsFooterLinkName,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookFooterLinkClicked - Error while hooking the analytics events',
      { sectionName },
      error,
    );
  }
};

/**
 * Function used on any section to append 'Query String API' for event 'Mini Navigation Clicked'
 *
 * The anchor element should have the following data attributes:
 * - data-analytics-<section_name>-mini-navigation-anchor-element
 * - data-analytics-<section_name>-cta-name
 * - data-analytics-<section_name>-cta-url
 *
 * @param {Object} param with several properties
 * @property {string} sectionName - The name of the section to hook the analytics events
 *
 * @returns {void}
 */
const hookMiniNavClicked = ({ sectionName }) => {
  if (ValidatorUtils.isNil(sectionName)) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookMiniNavClicked - sectionName is required',
      { sectionName },
      new Error('Missing required params'),
    );

    return;
  }

  try {
    // Get main section container
    const sectionContainer = _getMainSectionContainer(
      `[data-analytics-${sectionName}-section-container]`,
    );

    // Get all the CTAs in the mini navigation sub section
    const anchorElementsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-mini-navigation-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(anchorElementsList).forEach((ctaLink) => {
      const analyticsLinkCTAName = ctaLink.getAttribute(`data-analytics-${sectionName}-cta-name`);
      const analyticsLinkDestination = ctaLink.getAttribute(
        `data-analytics-${sectionName}-cta-url`,
      );

      const baseUrl = ctaLink.href;

      // Warn mandatory missed data attributes
      _warnMissedDataAttributesProps({
        propsObject: {
          analyticsLinkCTAName,
          analyticsLinkDestination,
          baseUrl,
        },
        sectionName,
      });

      const ctaUrlWithAnalytics = RudderstackClient.miniNavigationClickedQueryStringUrlBuilder({
        baseUrl,
        linkCTAName: analyticsLinkCTAName,
        linkDestination: analyticsLinkDestination,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookMiniNavClicked - Error while hooking the analytics events',
      { sectionName },
      error,
    );
  }
};

/**
 *  Function used to send event 'Add to Cart' to Rudderstack and GTM
 *
 * @param {Object} cart - The full cart object obtained from AJAX Cart API
 * @param {Object} productToUpdate - The product to be added to the cart - This could be a Shopify liquid product or AJAX Cart API product
 * @param {string} source - The source of the event - This will affect the parameter sent to Rudderstack productAddedAjaxCart or productAddedShopify
 * @param {string} sizeSelected - The size of the variant
 *
 * @returns {void}
 */
const triggerProductAdded = (cart, productToUpdate, source, sizeSelected) => {
  let productAddedAjaxCart = null;
  let productAddedShopify = null;

  // If the source is CART, UPSELL or QUICK_ADD, the product is from AJAX Cart API
  if (
    source === TRACK_CART_SOURCE.CART ||
    source === TRACK_CART_SOURCE.UPSELL ||
    source === TRACK_CART_SOURCE.QUICK_ADD
  ) {
    productAddedAjaxCart = productToUpdate;
  }

  // If the source is PDP, the product is from Shopify liquid product
  if (source === TRACK_CART_SOURCE.PDP) {
    productAddedShopify = productToUpdate;
  }

  // A Shopify liquid product or AJAX Cart API product is required
  if (!productAddedAjaxCart && !productAddedShopify) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerProductAdded -  productAddedAjaxCart and productAddedShopify are missing',
      { productAddedAjaxCart, productAddedShopify, source },
      new Error('Product data is missing'),
    );

    return;
  }

  // Send the event to Rudderstack
  RudderstackClient.productAdded({
    ajaxCartItems: cart?.items,
    productAddedAjaxCart,
    productAddedShopify,
    productAddedSource: source,
    productSizeSelected: sizeSelected || productToUpdate?.variant_title,
  });

  // Send the event to GTM
  gtmProductAdded(productToUpdate, cart, source);
};

/**
 *  Function used to send event 'Remove from Cart'
 *
 * @param {Object} cart the full cart object
 * @param {Object} productRemoved the product to be added to the cart
 * @param {number} currentQuantity the quantity of product to remove
 */
const triggerProductRemoved = (cart, productRemoved, currentQuantity) => {
  RudderstackClient.productRemoved({
    ajaxCartItems: cart?.items,
    productRemovedAjaxCart: productRemoved,
    productRemovedQuantity: currentQuantity,
  });
  gtmProductRemoved(productRemoved, cart);
};

/**
 *  Function used to send event 'Products Search Initiated'
 *
 * @param {Object} param - The search entry and source
 * @property {string} searchEntry - The search entry
 * @property {string} searchSource - The search source
 */
const triggerProductsSearchInitiated = ({ searchEntry, searchSource }) => {
  // Log a warning if some params are missing
  if (ValidatorUtils.isNil(searchEntry) || ValidatorUtils.isNil(searchSource)) {
    DataDogLogs.warn(
      '[Allbirds analytics] - Data Extraction - triggerProductsSearchInitiated - Missing some params',
      { searchEntry, searchSource },
    );
  }

  // Send the event to Rudderstack
  RudderstackClient.productsSearchInitiated({
    searchEntry,
    searchSource,
  });
};

/**
 *  Function used to send event 'Products Searched'
 *
 * @param {Object} param - The search query, entry and source
 * @property {string} query - The search query
 * @property {string} searchEntry - The search entry
 * @property {string} searchSource - The search source
 * @property {number} searchResultsCount - The number of search results
 */
const triggerProductsSearched = ({ query, searchEntry, searchResultsCount, searchSource }) => {
  try {
    // Log a warning if some params are missing
    if (
      ValidatorUtils.isNil(query) ||
      ValidatorUtils.isNil(searchEntry) ||
      ValidatorUtils.isNil(searchSource) ||
      ValidatorUtils.isNil(searchResultsCount)
    ) {
      DataDogLogs.warn(
        '[Allbirds analytics] - Data Extraction - triggerProductsSearched - Missing some params',
        { query, searchEntry, searchResultsCount, searchSource },
      );
    }

    // Send the event to Rudderstack
    RudderstackClient.productsSearched({
      query,
      searchEntry,
      searchResultsCount,
      searchSource,
    });

    // Build GTM payload from current search results DOM
    const searchResultsGrid = document.querySelector('[data-search-results-grid]');
    const productNodes = searchResultsGrid
      ? Array.from(searchResultsGrid.querySelectorAll('[data-product-json]'))
      : [];

    const searchResultsItems = productNodes
      .map((node) => {
        try {
          return JSON.parse(node.dataset.productJson);
        } catch (e) {
          DataDogLogs.error(
            '[Allbirds analytics] - Data Extraction - triggerProductsSearched - Error parsing product JSON',
            { productJson: node?.dataset?.productJson },
            e,
          );
          return null;
        }
      })
      .filter(Boolean)
      .map((product) => {
        const title = typeof product.title === 'string' ? product.title : '';
        const separator = ' - ';
        const index = title.indexOf(separator);
        const masterName = index >= 0 ? title.slice(0, index) : title;
        const color = index >= 0 ? title.slice(index + separator.length) : '';
        return {
          color,
          handle: product.handle,
          id: product.id,
          masterName,
          name: title,
        };
      });

    gtmProductsSearched({
      query,
      searchResultsCount,
      searchResultsItems,
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerProductsSearched - Error while triggering the products searched event',
      { query, searchEntry, searchSource },
      error,
    );
  }
};

/**
 *  Function used to send event 'Cart Viewed'
 *
 * @param {Object} items - The items in the cart
 */
const triggerOpenCartDrawer = (items) => {
  RudderstackClient.cartViewed({
    ajaxCartItems: items,
  });
  gtmCartViewed(items);
};

/**
 *  Function used to send event 'Experiment Viewed'
 *
 * @param {Object} analyticsMetadata - The analytics metadata object returned by Dynamic Yield API
 * @property {string} campaignId - The ID of the campaign
 * @property {string} campaignName - The name of the campaign
 * @property {string} experienceId - The ID of the experience
 * @property {string} experienceName - The name of the experience
 * @property {string} variationId - The ID of the variation
 * @property {string} variationName - The name of the variation
 *
 * @param {string} experimentType - The type of the experiment provided on constants/campaigns.js
 */
const triggerExperimentViewed = (analyticsMetadata, experimentType) => {
  if (ValidatorUtils.isNil(analyticsMetadata)) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerExperimentViewed - Missing analyticsMetadata',
      { analyticsMetadata, experimentType },
      new Error('Missing analyticsMetadata'),
    );

    return;
  }

  // Pull the data from the analytics metadata object
  const { campaignId, campaignName, experienceId, experienceName, variationId, variationName } =
    analyticsMetadata;

  // Log a warning if some params are missing
  if (
    ValidatorUtils.isNil(campaignId) ||
    ValidatorUtils.isNil(campaignName) ||
    ValidatorUtils.isNil(experienceId) ||
    ValidatorUtils.isNil(experienceName) ||
    ValidatorUtils.isNil(experimentType) ||
    ValidatorUtils.isNil(variationId) ||
    ValidatorUtils.isNil(variationName)
  ) {
    DataDogLogs.warn(
      '[Allbirds analytics] - Data Extraction - triggerExperimentViewed - Missing some params',
      {
        campaignId,
        campaignName,
        experienceId,
        experienceName,
        experimentType,
        variationId,
        variationName,
      },
    );
  }

  // Send the event to Rudderstack
  RudderstackClient.experimentViewed({
    campaignId,
    campaignName,
    experienceId,
    experienceName,
    experimentType,
    variationId,
    variationName,
  });
};

/**
 * Function used on any section to append 'Query String API' for event 'Call to Action Clicked'
 *
 * The anchor element should have the following data attributes:
 * - data-analytics-<section_name>-call-to-action-anchor-element
 * - data-analytics-<section_name>-call-to-action-campaign-experience-name
 * - data-analytics-<section_name>-call-to-action-campaign-name
 * - data-analytics-<section_name>-call-to-action-campaign-variation-id
 * - data-analytics-<section_name>-call-to-action-campaign-variation-name
 * - data-analytics-<section_name>-call-to-action-copy
 * - data-analytics-<section_name>-call-to-action-location
 * - data-analytics-<section_name>-call-to-action-type
 * - data-analytics-<section_name>-call-to-action-name
 * - data-analytics-<section_name>-call-to-action-url
 * - data-analytics-<section_name>-call-to-action-value
 *
 * @param {Object} param with several properties
 * @property {string} sectionName - The name of the section to hook the analytics events
 * @property {string} typeParam - The type of the call to action, constants on TRACK_GENERIC_CTA_CLICKED_EVENT.TYPES
 * @property {string} valueParam - The value of the call to action
 * @property {string} locationParam - The location of the call to action, constants on TRACK_GENERIC_CTA_CLICKED_EVENT.LOCATIONS
 *
 * @returns {void}
 */
const hookCallToActionClicked = ({ locationParam, sectionName, typeParam, valueParam }) => {
  if (ValidatorUtils.isNil(sectionName)) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookCallToActionClicked - sectionName is required',
      { sectionName },
      new Error('Missing required params'),
    );

    return;
  }

  try {
    // Get main section container
    const sectionContainer = _getMainSectionContainer(
      `[data-analytics-${sectionName}-section-container]`,
    );

    // Get all the CTAs in the section
    const anchorElementsList = sectionContainer.querySelectorAll(
      `[data-analytics-${sectionName}-call-to-action-anchor-element]`,
    );

    // For each CTA, get the data attributes and build the URL with the query string
    Array.from(anchorElementsList).forEach((ctaLink) => {
      // Campaign related data attributes
      const analyticsCampaignExperienceName = ctaLink.getAttribute(
        `data-analytics-${sectionName}-call-to-action-campaign-experience-name`,
      );
      const analyticsCampaignName = ctaLink.getAttribute(
        `data-analytics-${sectionName}-call-to-action-campaign-name`,
      );
      const analyticsCampaignVariationId = ctaLink.getAttribute(
        `data-analytics-${sectionName}-call-to-action-campaign-variation-id`,
      );
      const analyticsCampaignVariationName = ctaLink.getAttribute(
        `data-analytics-${sectionName}-call-to-action-campaign-variation-name`,
      );

      // Data specific to the call to action
      const analyticsCopy = ctaLink.getAttribute(
        `data-analytics-${sectionName}-call-to-action-copy`,
      );
      const analyticsName = ctaLink.getAttribute(
        `data-analytics-${sectionName}-call-to-action-name`,
      );
      const analyticsCtaUrl = ctaLink.getAttribute(
        `data-analytics-${sectionName}-call-to-action-url`,
      );

      // Data that can be passed as a parameter or from the data attributes
      const analyticsLocation =
        locationParam ||
        ctaLink.getAttribute(`data-analytics-${sectionName}-call-to-action-location`);

      const analyticsType =
        typeParam || ctaLink.getAttribute(`data-analytics-${sectionName}-call-to-action-type`);

      const analyticsValue =
        valueParam || ctaLink.getAttribute(`data-analytics-${sectionName}-call-to-action-value`);

      const baseUrl = ctaLink.href;

      // Warn mandatory missed data attributes, Tracking Plan: https://github.com/allbirds/rudderstack-resources/blob/main/tracking_plans/web/plan.json
      _warnMissedDataAttributesProps({
        propsObject: {
          analyticsLocation,
          analyticsName,
          analyticsType,
          baseUrl,
        },
        sectionName,
      });

      const ctaUrlWithAnalytics = RudderstackClient.callToActionClickedQueryStringUrlBuilder({
        baseUrl,
        campaignExperienceName: analyticsCampaignExperienceName,
        campaignName: analyticsCampaignName,
        campaignVariationId: analyticsCampaignVariationId,
        campaignVariationName: analyticsCampaignVariationName,
        copy: analyticsCopy,
        location: analyticsLocation,
        name: analyticsName,
        type: analyticsType,
        url: analyticsCtaUrl,
        value: analyticsValue,
      });

      // Only set the href if it is not empty otherwise leave the original href
      if (ctaUrlWithAnalytics) ctaLink.href = ctaUrlWithAnalytics;
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - hookCallToActionClicked - Error while hooking the analytics events',
      { sectionName },
      error,
    );
  }
};

/**
 * Function used to send event 'Product Image Clicked' to Rudderstack
 *
 * @param {Object} object with several properties
 * @property {number} pdpImagePosition - The position/index of the image (0-based)
 * @property {Object} shopifyProduct - Allbirds product representation object, check 'utils.js-product-object.liquid'
 *
 * @returns {void}
 */
const triggerProductImageClicked = ({ pdpImagePosition, shopifyProduct }) => {
  // Try to send the event to Rudderstack
  try {
    RudderstackClient.productImageClicked({
      pdpImagePosition,
      shopifyProduct,
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerProductImageClicked - Error while triggering the product image clicked event',
      { pdpImagePosition, shopifyProduct },
      error,
    );
  }

  // Send event to GTM if needed
};

/**
 * Function used to send event 'Product Image Scroll' to Rudderstack
 *
 * @param {Object} object with several properties
 * @property {number} pdpImagePosition - The position/index of the image (0-based)
 * @property {Object} shopifyProduct - Allbirds product representation object, check 'utils.js-product-object.liquid'
 *
 * @returns {void}
 */
const triggerProductImageScroll = ({ pdpImagePosition, shopifyProduct }) => {
  // Send the event to Rudderstack
  try {
    RudderstackClient.productImageScroll({
      pdpImagePosition,
      shopifyProduct,
    });
  } catch (error) {
    DataDogLogs.error(
      '[Allbirds analytics] - Data Extraction - triggerProductImageScroll - Error while triggering the product image scroll event',
      { pdpImagePosition, shopifyProduct },
      error,
    );
  }

  // Send event to GTM if needed
};

export {
  hookCallToActionClicked,
  hookFooterLinkClicked,
  hookGlobalBannerLinkClicked,
  hookHomepageHeroClicked,
  hookHomepageModuleClicked,
  hookMiniNavClicked,
  hookNavigationClicked,
  hookProductListClicked,
  hookProductListFeatureClicked,
  triggerExperimentViewed,
  triggerNavigationViewed,
  triggerOpenCartDrawer,
  triggerProductAdded,
  triggerProductImageClicked,
  triggerProductImageScroll,
  triggerProductListFeatureClicked,
  triggerProductListFiltered,
  triggerProductListViewed,
  triggerProductRemoved,
  triggerProductsSearched,
  triggerProductsSearchInitiated,
  triggerProductViewed,
};
